/**
 * Hook: usePermissions
 *
 * Fetches the current user's effective permissions (the merge of their role
 * template + any per-row overrides the backend applies). Cached for 5 minutes
 * because role permissions change rarely and a stale check is vastly cheaper
 * than hitting the API on every Sidebar render.
 */

import { useQuery } from '@tanstack/react-query'
import { get } from '@/lib/api'
import { useAuthStore } from '@/stores/auth'

export type Role = 'SUPER_ADMIN' | 'TENANT_ADMIN' | 'MANAGER' | 'AGENT' | 'READ_ONLY'
export type DataScope = 'own' | 'team' | 'all'

/**
 * Shape of the server's RolePermission row. All canXxx fields must exist on
 * the Prisma model; keep this in sync with
 * apps/api/prisma/schema.prisma → model RolePermission.
 */
export interface RolePermission {
  role: Role
  // Module visibility
  canViewDashboard: boolean
  canViewCases: boolean
  canViewContacts: boolean
  canViewAccounts: boolean
  canViewLeads: boolean
  canViewOpportunities: boolean
  canViewCampaigns: boolean
  canViewKb: boolean
  canViewReports: boolean
  canViewChannels: boolean
  canViewSettings: boolean
  canViewTeams: boolean
  // Action permissions
  canCreateRecords: boolean
  canEditRecords: boolean
  canDeleteRecords: boolean
  canExportData: boolean
  canImportData: boolean
  canManageTeam: boolean
  canInviteUsers: boolean
  canAssignRecords: boolean
  canManageCampaigns: boolean
  canManageKb: boolean
  // Data scope
  dataScope: DataScope
}

export type PermissionKey = keyof Omit<RolePermission, 'role' | 'dataScope'>

/**
 * TENANT_ADMIN and SUPER_ADMIN bypass the server-side check, but the client
 * still needs to hide/show nav items instantly before the fetch resolves.
 * We seed a permissive default for admins so the first paint is correct.
 */
const ADMIN_DEFAULT: RolePermission = {
  role: 'TENANT_ADMIN',
  canViewDashboard: true, canViewCases: true, canViewContacts: true,
  canViewAccounts: true, canViewLeads: true, canViewOpportunities: true,
  canViewCampaigns: true, canViewKb: true, canViewReports: true,
  canViewChannels: true, canViewSettings: true, canViewTeams: true,
  canCreateRecords: true, canEditRecords: true, canDeleteRecords: true,
  canExportData: true, canImportData: true, canManageTeam: true,
  canInviteUsers: true, canAssignRecords: true, canManageCampaigns: true,
  canManageKb: true, dataScope: 'all',
}

export function usePermissions() {
  const user = useAuthStore((s) => s.user)
  const role = (user?.role ?? 'AGENT') as Role
  const isAdmin   = role === 'TENANT_ADMIN' || role === 'SUPER_ADMIN'
  const isManager = role === 'MANAGER'
  const isAgent   = role === 'AGENT'

  const { data, isLoading } = useQuery({
    queryKey: ['permissions', 'my'],
    queryFn: () => get<RolePermission>('/api/v1/permissions/my'),
    // Server is authoritative, but role profiles change rarely — a 5 minute
    // stale window is well within reasonable UX trade-off.
    staleTime: 5 * 60_000,
    enabled: !!user,
  })

  // Fall back to a permissive admin default while the fetch is in flight OR
  // if the server errors — we'd rather show all nav items than hide them all.
  const permissions = data ?? (isAdmin ? ADMIN_DEFAULT : null)

  function can(perm: PermissionKey): boolean {
    if (isAdmin) return true
    if (!permissions) return false
    return !!permissions[perm]
  }

  function canView(module: string): boolean {
    // Accept both 'cases' and 'canViewCases' for ergonomics.
    if (module.startsWith('canView')) return can(module as PermissionKey)
    const camel = module.charAt(0).toUpperCase() + module.slice(1)
    const key = `canView${camel}` as PermissionKey
    return can(key)
  }

  return {
    permissions,
    isLoading,
    role,
    isAdmin,
    isManager,
    isAgent,
    can,
    canView,
  }
}
